<template>
    <div class="campaigns-list">
        <div class="header">
            <div class="title-block">
                <div class="title">{{ $t("metaInsights.campaigns") }}</div>
                <div class="subtitle">{{ $t("metaInsights.campaignsDesc") }}</div>
            </div>
            <div class="toolbar">
                <DateRangePicker v-model="dateRange" @change="loadCampaigns" />
                <SupsisButton ghost @click="loadCampaigns" :disabled="loading">
                    <i class="material-icons" :class="{ spinning: loading }">refresh</i>
                    {{ $t("metaInsights.refresh") }}
                </SupsisButton>
            </div>
        </div>

        <a-spin :spinning="loading">
            <InsightsTable
                :rows="campaigns"
                :currency="currency"
                table-id="meta-insights-campaigns-table"
                :name-label-key="'metaInsights.col.campaign'"
                :clickable="true"
                @row-click="onRowClick"
            />
        </a-spin>

        <div v-if="hasMore" class="load-more-wrap">
            <SupsisButton ghost :disabled="loadingMore" @click="loadMore">
                <i v-if="loadingMore" class="material-icons spinning">refresh</i>
                <i v-else class="material-icons">expand_more</i>
                {{ loadingMore ? $t("metaInsights.loading") : $t("metaInsights.loadMore") }}
            </SupsisButton>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import DateRangePicker from "./DateRangePicker.vue";
import InsightsTable from "./InsightsTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { INSIGHTS_API } from "../../../../../api/meta";

export default {
    name: "CampaignsList",
    components: {
        DateRangePicker,
        InsightsTable,
        SupsisButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            dateRange: { datePreset: "last_30d" },
            campaigns: [],
            loading: false,
            loadingMore: false,
            afterCursor: null,
            hasMore: false,
            pageSize: 25,
        };
    },
    computed: {
        currency() {
            return _.get(this.channel, "payload.selectedAdAccount.currency", "");
        },
    },
    methods: {
        async loadCampaigns(append) {
            if (!this.channel) return;
            const isAppend = !!append;
            if (isAppend) {
                this.loadingMore = true;
            } else {
                this.loading = true;
                this.afterCursor = null;
            }
            try {
                const params = Object.assign({}, this.dateRange, {
                    limit: this.pageSize,
                    after: isAppend ? this.afterCursor : null,
                });
                const res = await INSIGHTS_API.listCampaigns(this.channel._id, params);
                const items = _.get(res, "data.data") || [];
                const paging = _.get(res, "data.paging") || {};
                this.campaigns = isAppend ? this.campaigns.concat(items) : items;
                this.afterCursor = _.get(paging, "cursors.after") || null;
                this.hasMore = !!paging.next;
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("metaInsights.failedToLoadCampaigns"));
                if (!isAppend) this.campaigns = [];
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },
        loadMore() {
            this.loadCampaigns(true);
        },
        onRowClick(row) {
            this.$emit("drill-down", { type: "campaign", row });
        },
    },
    mounted() {
        this.loadCampaigns();
    },
};
</script>

<style scoped lang="scss">
.campaigns-list {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--color-border-default);

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 13px;
                color: var(--color-text-muted-default);
            }
        }

        .toolbar {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .spinning {
        animation: spin 1s linear infinite;
    }

    .load-more-wrap {
        display: flex;
        justify-content: center;
        padding: 12px 0 4px;
    }
}
</style>
